import { getAPI, postAPI } from "../backend/commons";
import { localDatabaseUrls } from "../backend/constants";
import { getJWTToken } from "../shared/fetchWrapper/fetchWrapper";
import AlertManager from "../shared/Alert/Alert";
import ExcelJS from "exceljs";
import JSZip from "jszip";
import { getRuleLogs } from "./ruleLogsApi";

// Mock data for development
const MOCK_MARGIN_OPTIONS = {
  category: [
    "N/A",
    "PBE & Other",
    "Filters & Heavy Duty",
    "Commodities & Chemicals",
    "Heating & Cooling",
    "INVALID",
    "Electrical & Engine Systems",
    "Tools & Equipment",
    "Accessories",
    "Under Car",
    "UNKNOWN",
  ],
  hq: [
    "PCQ",
    "HCA",
    "LUH",
    "VIS",
    "LHQ",
    "ESB",
    "BEL",
    "TSS",
    "NBB",
    "OTC",
    "NNE",
  ],
  item: [
    "Laptop-Dell-XPS-13",
    "Monitor-Samsung-27",
    "Mouse-Logitech-MX",
    "Keyboard-Mechanical-RGB",
    "Headphones-Sony-WH1000XM4",
    "Webcam-Logitech-C920",
    "Desk-Standing-Adjustable",
    "Chair-Herman-Miller-Aeron",
    "Printer-HP-LaserJet",
    "Router-Netgear-Nighthawk",
    "Speaker-Bose-SoundLink",
    "Tablet-iPad-Pro-12",
    "Phone-iPhone-15-Pro",
    "Charger-Anker-PowerPort",
    "Cable-USBC-Braided",
  ],
};

const extractAutocompleteRecords = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object") {
    const candidates = ["queryResults", "elements", "data", "rows", "content", "results"];
    for (const key of candidates) {
      if (Array.isArray(payload[key])) {
        return payload[key];
      }
    }
  }

  return [];
};

const buildAutocompleteLabel = (value, groupCode = "") => {
  if (!value) {
    return "";
  }

  return groupCode ? `${value} | ${groupCode}` : value;
};

const getStringField = (record, keyMatchers = []) => {
  if (!record || typeof record !== "object") {
    return "";
  }

  for (const [key, value] of Object.entries(record)) {
    if (typeof value !== "string" || !value.trim()) {
      continue;
    }

    if (keyMatchers.some((matcher) => (matcher instanceof RegExp ? matcher.test(key) : key === matcher))) {
      return value.trim();
    }
  }

  return "";
};

const normalizeAutocompleteOption = (record) => {
  if (typeof record === "string") {
    const trimmedValue = record.trim();

    return trimmedValue ? { value: trimmedValue, label: trimmedValue } : null;
  }

  if (record && typeof record === "object") {
    const groupCode =
      getStringField(record, [
        "groupCode",
        "group_code",
        "groupcode",
        "groupId",
        "group_id",
        "group",
        "grpCode",
        "grp_code",
        /group.*code/i,
        /.*group$/i,
        /.*code$/i,
      ]) ||
      "";
    const rawValue =
      getStringField(record, ["scopeValue", "itemSku", "value", "name", "text", "label", "description", "sku"]) ||
      "";
    const baseValue = rawValue || record.label || "";

    if (!baseValue) {
      return null;
    }

    return {
      value: rawValue || baseValue,
      label: buildAutocompleteLabel(baseValue, groupCode),
    };
  }

  return null;
};

const normalizeAutocompleteOptions = (payload) =>
  Array.from(
    new Map(
      extractAutocompleteRecords(payload)
        .map(normalizeAutocompleteOption)
        .filter((option) => option && typeof option.value === "string" && option.value.trim().length > 0)
        .map((option) => [option.value, option])
    ).values()
  );

const FIELD_TYPE_BY_SCOPE = {
  item: "part_number",
  category: "category",
  hq: "hq_line",
  group_code: "group_code",
};

export const MARGIN_RULE_TEMPLATE_HEADERS = ["Category", "HQ", "Item", "Group Code", "Margin Strategy"];
export const MARGIN_RULE_TEMPLATE_STRATEGIES = [
  "GP $ neutral",
  "Maintain price",
  "Maintain margin",
];
export const SINGLE_PRICE_TEMPLATE_HEADERS = ["SKU", "HQ LINE", "SINGLE PRICE", "ENABLED (YES/NO)"];
export const SINGLE_PRICE_TEMPLATE_OPTIONS = ["Yes", "No"];
export const MARGIN_RULE_LOG_EXPORT_HEADERS = [
  "Category",
  "HQ",
  "Item",
  "Group Code",
  "Margin strategy",
  "Set by",
  "Set Date",
];
export const SINGLE_PRICE_LOG_EXPORT_HEADERS = [
  "SKU",
  "HQ LINE",
  "SINGLE PRICE",
  "ENABLED (YES/NO)",
  "Set by",
  "Set Date",
];

const STRATEGY_LABELS = {
  gpneutral: "GP $ neutral",
  maintainprice: "Maintain price",
  maintainmargin: "Maintain margin",
  singleprice: "Single price",
};

const formatMarginStrategyLabel = (strategy) => {
  if (typeof strategy !== "string" || !strategy.trim()) {
    return "";
  }

  const normalizedStrategy = strategy.replace(/[^a-z0-9]+/gi, "").toLowerCase();
  if (STRATEGY_LABELS[normalizedStrategy]) {
    return STRATEGY_LABELS[normalizedStrategy];
  }

  return strategy
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

const normalizeHeaderName = (value) => String(value ?? "").trim().toLowerCase();

const getCellText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }

  if (Array.isArray(value?.richText)) {
    return value.richText.map((part) => part?.text || "").join("").trim();
  }

  if (typeof value === "object") {
    if (typeof value.text === "string") {
      return value.text.trim();
    }

    if (value.result !== undefined && value.result !== null) {
      return String(value.result).trim();
    }

    if (value.value !== undefined && value.value !== value) {
      return getCellText(value.value);
    }
  }

  return String(value).trim();
};

const getXmlDocument = (xmlString) => new DOMParser().parseFromString(xmlString, "application/xml");

const getColumnLetters = (cellReference) => String(cellReference ?? "").replace(/\d+/g, "");

const getColumnIndexFromLetters = (columnLetters) => {
  const normalizedLetters = String(columnLetters ?? "").toUpperCase();
  let columnIndex = 0;

  for (const character of normalizedLetters) {
    if (character < "A" || character > "Z") {
      continue;
    }

    columnIndex = columnIndex * 26 + (character.charCodeAt(0) - 64);
  }

  return columnIndex;
};

const getFileExtension = (fileName = "") => {
  const normalizedFileName = String(fileName).toLowerCase();
  const extensionIndex = normalizedFileName.lastIndexOf(".");

  return extensionIndex >= 0 ? normalizedFileName.slice(extensionIndex) : "";
};

const detectDelimitedTextSeparator = (text) => {
  const sampleLine = String(text ?? "")
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .find((line) => line.trim().length > 0) || "";

  const separators = [",", ";", "\t", "|"];

  return separators.reduce(
    (bestMatch, separator) => {
      const matchCount = sampleLine.split(separator).length - 1;
      return matchCount > bestMatch.matchCount ? { separator, matchCount } : bestMatch;
    },
    { separator: ",", matchCount: 0 }
  ).separator;
};

const parseDelimitedText = (text, separator) => {
  const normalizedText = String(text ?? "").replace(/^\uFEFF/, "");
  const rows = [];
  let currentRow = [];
  let currentCell = "";
  let isInsideQuotes = false;

  for (let index = 0; index < normalizedText.length; index += 1) {
    const character = normalizedText[index];
    const nextCharacter = normalizedText[index + 1];

    if (isInsideQuotes) {
      if (character === '"') {
        if (nextCharacter === '"') {
          currentCell += '"';
          index += 1;
        } else {
          isInsideQuotes = false;
        }
      } else {
        currentCell += character;
      }

      continue;
    }

    if (character === '"') {
      isInsideQuotes = true;
      continue;
    }

    if (character === separator) {
      currentRow.push(currentCell);
      currentCell = "";
      continue;
    }

    if (character === "\r") {
      continue;
    }

    if (character === "\n") {
      currentRow.push(currentCell);
      rows.push(currentRow);
      currentRow = [];
      currentCell = "";
      continue;
    }

    currentCell += character;
  }

  currentRow.push(currentCell);
  rows.push(currentRow);

  return rows.filter((row) => row.some((cell) => String(cell ?? "").trim().length > 0));
};

const getWorksheetRowsFromXlsxFile = async (file) => {
  const fileBuffer = typeof file.arrayBuffer === "function" ? await file.arrayBuffer() : await new Response(file).arrayBuffer();
  const workbookZip = await JSZip.loadAsync(fileBuffer);
  const worksheetFile = workbookZip.file("xl/worksheets/sheet1.xml");

  if (!worksheetFile) {
    return [];
  }

  const sharedStrings = await getSharedStrings(workbookZip);
  const worksheetXml = await worksheetFile.async("string");
  const worksheetDocument = getXmlDocument(worksheetXml);
  const rowNodes = Array.from(worksheetDocument.getElementsByTagName("row"));

  return rowNodes.map((rowNode) => {
    const rowValues = [];

    Array.from(rowNode.getElementsByTagName("c")).forEach((cellNode) => {
      const columnIndex = getColumnIndexFromLetters(getColumnLetters(cellNode.getAttribute("r"))) - 1;
      if (columnIndex >= 0) {
        rowValues[columnIndex] = getCellValueFromXmlNode(cellNode, sharedStrings);
      }
    });

    return rowValues;
  });
};

const getWorksheetRowsFromFile = async (file) => {
  const fileExtension = getFileExtension(file?.name);

  if (fileExtension === ".csv") {
    const fileText = typeof file.text === "function" ? await file.text() : await new Response(file).text();
    return parseDelimitedText(fileText, detectDelimitedTextSeparator(fileText));
  }

  return getWorksheetRowsFromXlsxFile(file);
};

const getCellValueFromXmlNode = (cellNode, sharedStrings) => {
  const cellType = cellNode.getAttribute("t");

  if (cellType === "s") {
    const sharedStringIndex = Number(cellNode.getElementsByTagName("v")[0]?.textContent ?? NaN);
    return sharedStrings[sharedStringIndex] || "";
  }

  if (cellType === "inlineStr") {
    return Array.from(cellNode.getElementsByTagName("t"))
      .map((textNode) => textNode.textContent || "")
      .join("");
  }

  return cellNode.getElementsByTagName("v")[0]?.textContent || "";
};

const getSharedStrings = async (zip) => {
  const sharedStringsFile = zip.file("xl/sharedStrings.xml");
  if (!sharedStringsFile) {
    return [];
  }

  const sharedStringsXml = await sharedStringsFile.async("string");
  const sharedStringsDocument = getXmlDocument(sharedStringsXml);

  return Array.from(sharedStringsDocument.getElementsByTagName("si")).map((sharedStringNode) =>
    Array.from(sharedStringNode.getElementsByTagName("t"))
      .map((textNode) => textNode.textContent || "")
      .join("")
  );
};

const validateAlphabetOnlyColumn = async (file, headerNames, columnLabel) => {
  if (!file) {
    return null;
  }

  try {
    const fileExtension = getFileExtension(file?.name);
    if (fileExtension === ".xls") {
      return null;
    }

    const worksheetRows = await getWorksheetRowsFromFile(file);
    if (!worksheetRows.length) {
      return `Uploaded file is invalid. Please use the template for ${columnLabel.toLowerCase()}.`;
    }

    const headerRow = worksheetRows[0] || [];
    const targetColumnIndex = headerNames
      .map((headerName) =>
        headerRow.findIndex((cellValue) => normalizeHeaderName(getCellText(cellValue)) === normalizeHeaderName(headerName))
      )
      .find((columnIndex) => columnIndex >= 0);

    if (targetColumnIndex === undefined) {
      return `Uploaded file must contain the ${columnLabel} column.`;
    }

    let invalidRow = null;

    for (const [rowIndex, rowValues] of worksheetRows.slice(1).entries()) {
      if (invalidRow) {
        break;
      }

      const cellValue = getCellText(rowValues[targetColumnIndex]);
      if (!cellValue) {
        continue;
      }

      if (!cellValue.trim()) {
        invalidRow = rowIndex + 2;
      }
    }

    if (invalidRow) {
      return `${columnLabel} is required.`;
    }

    return null;
  } catch (error) {
    console.error(`Error validating ${columnLabel} values:`, error);
    return `Unable to validate the ${columnLabel} column. Please use the template.`;
  }
};

export const addAlphabetOnlyValidation = (worksheet, columnLetter, columnLabel) => {
  worksheet.dataValidations.add(`${columnLetter}2:${columnLetter}1048576`, {
    type: "custom",
    allowBlank: true,
    formulae: [`LEN(TRIM(${columnLetter}2))>0`],
    showInputMessage: true,
    promptTitle: `${columnLabel} required`,
    prompt: `Enter a value in ${columnLabel}.`,
    showErrorMessage: true,
    errorTitle: `${columnLabel} required`,
    error: `${columnLabel} is required.`,
    errorStyle: "stop",
  });
};

const getMarginDimensionValue = (row, dimension) => {
  const rowScope = row?.scope?.toLowerCase();
  const dimensionFieldMap = {
    hq: ["hq", "hqLine"],
    category: ["category"],
    item: ["item", "itemSku"],
  };

  const fieldCandidates = dimensionFieldMap[dimension] || [dimension];

  for (const fieldName of fieldCandidates) {
    if (row?.[fieldName]) {
      return row[fieldName];
    }
  }

  if (rowScope === dimension) {
    if (dimension === "item") {
      return row?.itemSku || row?.scopeValue || "";
    }

    return row?.scopeValue || "";
  }

  return "";
};

const getMarginGroupCodeValue = (row) => row?.groupCode || row?.group_code || row?.groupcode || row?.group || row?.groupId || "";

const MARGIN_RULE_LOG_EXPORT_COLUMNS = [
  { header: "Category", value: (row) => getMarginDimensionValue(row, "category") },
  { header: "HQ", value: (row) => getMarginDimensionValue(row, "hq") },
  { header: "Item", value: (row) => getMarginDimensionValue(row, "item") },
  { header: "Group Code", value: (row) => getMarginGroupCodeValue(row) },
  {
    header: "Margin strategy",
    value: (row) => formatMarginStrategyLabel(row?.marginStrategy || row?.strategy || ""),
  },
  {
    header: "Set by",
    value: (row) => row?.changedBy || row?.setBy || "",
  },
  {
    header: "Set Date",
    value: (row) => {
      const dateStr = row?.changedDate || row?.setDate;
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
  },
];

const SINGLE_PRICE_LOG_EXPORT_COLUMNS = [
  { header: "SKU", value: (row) => row?.sku || row?.parsedSku || row?.napaSku || row?.itemSku || "" },
  { header: "HQ LINE", value: (row) => row?.hqLine || row?.hq_line || row?.hq || "" },
  {
    header: "SINGLE PRICE",
    value: (row) => row?.singlePrice ?? row?.price ?? row?.valueText ?? row?.value ?? "",
  },
  {
    header: "ENABLED (YES/NO)",
    value: (row) => {
      const enabledValue = row?.enabled ?? row?.isEnabled ?? row?.active ?? row?.status;
      return enabledValue === true || enabledValue === 1 || enabledValue === "1" || enabledValue === "true" || enabledValue === "TRUE" || enabledValue === "yes" || enabledValue === "YES" || enabledValue === "y" || enabledValue === "Y"
        ? "Yes"
        : enabledValue === false || enabledValue === 0 || enabledValue === "0" || enabledValue === "false" || enabledValue === "FALSE" || enabledValue === "no" || enabledValue === "NO" || enabledValue === "n" || enabledValue === "N"
          ? "No"
          : enabledValue
            ? String(enabledValue)
            : "";
    },
  },
  {
    header: "Set by",
    value: (row) => row?.createdBy || row?.setBy || row?.changedBy || "",
  },
  {
    header: "Set Date",
    value: (row) => {
      const dateStr = row?.createdDate || row?.setDate || row?.changedDate;
      if (!dateStr) return "";

      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
  },
];

const csvEscape = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const extractMarginRulePageRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.rows)) {
    return payload.rows;
  }

  if (Array.isArray(payload?.content)) {
    return payload.content;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.data?.content)) {
    return payload.data.content;
  }

  return [];
};

const fetchAllMarginRuleLogs = async () => {
  const pageSize = 1000;
  const firstPage = await getRuleLogs("margin", { page: 0, size: pageSize });
  const firstPageRows = extractMarginRulePageRows(firstPage);
  const totalPages = Number(firstPage?.totalPages ?? 0);

  if (!totalPages || totalPages <= 1) {
    return firstPageRows;
  }

  const allRows = [...firstPageRows];
  for (let page = 1; page < totalPages; page += 1) {
    const pageData = await getRuleLogs("margin", { page, size: pageSize });
    allRows.push(...extractMarginRulePageRows(pageData));
  }

  return allRows;
};

const normalizeSinglePriceRows = (payload) =>
  extractMarginRulePageRows(payload).map((row, index) => ({
    ...row,
    id:
      row?.id ??
      row?.seqId ??
      row?.detailId ??
      row?.sku ??
      row?.parsedSku ??
      row?.napaSku ??
      `single-price-${index}`,
  }));

const fetchAllSinglePriceLogs = async () => {
  const pageSize = 1000;
  const firstPage = await getSinglePriceRowsPage({ page: 0, size: pageSize });
  const firstPageRows = firstPage.rows ?? [];
  const totalPages = Number(firstPage?.totalPages ?? 0);

  if (!totalPages || totalPages <= 1) {
    return firstPageRows;
  }

  const allRows = [...firstPageRows];
  for (let page = 1; page < totalPages; page += 1) {
    const pageData = await getSinglePriceRowsPage({ page, size: pageSize });
    allRows.push(...(pageData.rows ?? []));
  }

  return allRows;
};

export const getSinglePriceRows = async () => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return [];
  }

  const endpoint = localDatabaseUrls.serviceURL.skuSinglePrice;
  if (!endpoint) {
    AlertManager.error("Unable to load single price rules.");
    return [];
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${endpoint}`;

  try {
    const response = await getAPI(apiUrl, tokenInfo);
    if (response?.ok || response?.status === 200) {
      const payload = await response.json().catch(() => []);
      return normalizeSinglePriceRows(payload);
    }

    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to load single price rules.");
  } catch (error) {
    console.error("Error loading single price rules:", error);
    AlertManager.error(error?.message || "Unable to load single price rules.", {
      contactSupport: false,
    });
    return [];
  }
};

export const getSinglePriceRowsPage = async ({ page = 0, size = 10 } = {}) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return { rows: [], currentPage: 0, totalPages: 0, totalElements: 0 };
  }

  const endpoint = localDatabaseUrls.serviceURL.skuSinglePricePaginated || "api/v1/sku-single-price/paginated";
  if (!endpoint) {
    AlertManager.error("Unable to load single price rules.");
    return { rows: [], currentPage: 0, totalPages: 0, totalElements: 0 };
  }

  const requestPage = Number.isFinite(Number(page)) ? Number(page) : 0;
  const queryParams = new URLSearchParams({ page: String(requestPage), size: String(size) });
  const apiUrl = `${localDatabaseUrls.baseURL}${endpoint}?${queryParams.toString()}`;

  try {
    const response = await getAPI(apiUrl, tokenInfo);
    if (response?.ok || response?.status === 200) {
      const payload = await response.json().catch(() => []);
      const rows = normalizeSinglePriceRows(payload);
      const totalElements = Number(payload?.totalElements ?? payload?.totalElementsCount ?? rows.length);
      const derivedTotalPages = Number.isFinite(totalElements) && totalElements > 0
        ? Math.ceil(totalElements / Math.max(1, Number(size) || 10))
        : 0;

      return {
        rows,
        currentPage: Math.max(0, Number(payload?.currentPage ?? payload?.page ?? requestPage)),
        totalPages: Math.max(Number(payload?.totalPages ?? 0), derivedTotalPages),
        totalElements,
      };
    }

    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to load single price rules.");
  } catch (error) {
    console.error("Error loading single price rules:", error);
    AlertManager.error(error?.message || "Unable to load single price rules.", {
      contactSupport: false,
    });
    return { rows: [], currentPage: 0, totalPages: 0, totalElements: 0 };
  }
};

export const getMarginAutocompleteFieldType = (scopeType) => FIELD_TYPE_BY_SCOPE[scopeType] || null;

/**
 * Get margin rule options for dropdowns (category/hq/item)
 * @param {string} type - The type of options to fetch: 'category', 'hq', or 'item'
 * @returns {Promise<Array>} Array of options
 */
export const getMarginRuleOptions = async (type) => {
  try {
    // Mock implementation - remove this and uncomment API call when backend is ready
    await new Promise((resolve) => setTimeout(resolve, 300)); // Simulate network delay
    return MOCK_MARGIN_OPTIONS[type] || [];

    // Real API implementation (uncomment when backend is ready):
    // const response = await getAPI(
    //   `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.marginRuleOptions}?type=${type}`
    // );
    // return response.data || [];
  } catch (error) {
    console.error("Error fetching margin rule options:", error);
    throw error;
  }
};

/**
 * Fetch autocomplete suggestions for margin scope values.
 * @param {Object} payload - Search payload.
 * @param {string} payload.fieldType - Selected field type.
 * @param {string} payload.searchTerm - Search text.
 * @returns {Promise<Array>} Array of matching scope values.
 */
export const getMarginStrategyAutocomplete = async ({ fieldType, searchTerm } = {}) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return [];
  }

  if (!fieldType || !searchTerm) {
    return [];
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.marginStrategyAutocomplete}`;
  const queryParams = new URLSearchParams({ fieldType, searchTerm });

  try {
    const response = await getAPI(`${apiUrl}?${queryParams.toString()}`, tokenInfo);
    if (response?.status === 200 || response?.ok) {
      const payload = await response.json().catch(() => []);
      return normalizeAutocompleteOptions(payload);
    }

    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to load autocomplete suggestions.");
  } catch (error) {
    AlertManager.error(error?.message || "Unable to load autocomplete suggestions.", {
      contactSupport: false,
    });
    return [];
  }
};

/**
 * Save a single margin rule
 * @param {Object} payload - The margin rule data
 * @param {string} payload.scope - The scope type: 'category', 'hq', or 'item'
 * @param {string} payload.scopeValue - The value for category/hq scopes
 * @param {string} payload.itemSku - The value for item scope
 * @param {string} payload.strategy - The margin strategy: 'gpNeutral', 'maintainPrice', 'maintainMargin', or 'singlePrice'
 * @param {string} payload.changedBy - User who changed the rule
 * @param {string} payload.setDate - Date when rule was set
 * @returns {Promise<Object>} Response data
 */
export const saveMarginRule = async (payload) => {
  try {
    // Mock implementation - remove this and uncomment API call when backend is ready
    await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate network delay
    return {
      success: true,
      message: "Margin rule saved successfully",
      data: payload,
    };

    // Real API implementation (uncomment when backend is ready):
    // const response = await putAPI(
    //   `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.marginRuleSave}`,
    //   payload
    // );
    // return response.data;
  } catch (error) {
    console.error("Error saving margin rule:", error);
    throw error;
  }
};

/**
 * Upload bulk margin rules via Excel file
 * @param {File} file - The Excel file to upload
 * @param {string} uploadedBy - User who uploaded the file
 * @param {string} userEmail - Email of the user uploading the file
 * @returns {Promise<Object>} Response data
 */
export const uploadMarginRuleFile = async (file, uploadedBy, userEmail) => {
  const validationError = await validateAlphabetOnlyColumn(file, ["HQ"], "HQ");
  if (validationError) {
    AlertManager.error(validationError, { contactSupport: false });
    return null;
  }

  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const resolvedUserEmail = userEmail || tokenInfo.email || tokenInfo.username || uploadedBy || "";
  const resolvedUploadedBy = uploadedBy || resolvedUserEmail;
  const queryParams = new URLSearchParams();

  if (resolvedUploadedBy) {
    queryParams.set("uploadedBy", resolvedUploadedBy);
  }

  if (resolvedUserEmail) {
    queryParams.set("userEmail", resolvedUserEmail);
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.marginStrategyUpload}${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await postAPI(apiUrl, formData, tokenInfo);
    if (response?.ok) {
      return response;
    }

    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to upload margin rules.");
  } catch (error) {
    console.error("Error uploading margin rule file:", error);
    AlertManager.error(error?.message || "Unable to upload margin rules.", {
      contactSupport: false,
    });
    return null;
  }
};

/**
 * Upload single price rules via Excel file
 * @param {File} file - The Excel file to upload
 * @returns {Promise<Object>} Response data
 */
export const uploadSinglePriceFile = async (file) => {
  const validationError = await validateAlphabetOnlyColumn(file, ["HQ LINE"], "HQ line");
  if (validationError) {
    AlertManager.error(validationError, { contactSupport: false });
    return null;
  }

  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const uploadedBy = tokenInfo.email || tokenInfo.username;
  if (!uploadedBy) {
    AlertManager.error("User email is required", { contactSupport: false });
    return null;
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.skuSinglePriceUpload}?uploadedBy=${encodeURIComponent(uploadedBy)}`;
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await postAPI(apiUrl, formData, tokenInfo);
    if (response?.ok) {
      return response;
    }

    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to upload single price file.");
  } catch (error) {
    console.error("Error uploading single price file:", error);
    AlertManager.error(error?.message || "Unable to upload single price file.", {
      contactSupport: false,
    });
    return null;
  }
};

/**
 * Download margin rule template file as XLSX with headers only.
 * @returns {Promise<void>}
 */
export const downloadMarginRuleTemplate = async () => {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Margin Rules");
    worksheet.addRow(MARGIN_RULE_TEMPLATE_HEADERS);
    worksheet.getColumn(4).numFmt = "@";
    addAlphabetOnlyValidation(worksheet, "B", "HQ");
    worksheet.dataValidations.add("E2:E1048576", {
      type: "list",
      allowBlank: true,
      formulae: [`"${MARGIN_RULE_TEMPLATE_STRATEGIES.join(",")}"`],
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "margin_rule_template.xlsx";
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Real API implementation (uncomment when backend is ready):
    // const response = await getAPI(
    //   `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.marginRuleTemplate}`,
    //   { responseType: "blob" }
    // );
    //
    // const blob = new Blob([response.data], {
    //   type: "text/csv;charset=utf-8;",
    // });
    // const link = document.createElement("a");
    // const url = URL.createObjectURL(blob);
    //
    // link.setAttribute("href", url);
    // link.setAttribute("download", "margin_rule_template.csv");
    // link.style.visibility = "hidden";
    // document.body.appendChild(link);
    // link.click();
    // document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading margin rule template:", error);
    throw error;
  }
};

/**
 * Download single price template file as XLSX with headers only.
 * @returns {Promise<void>}
 */
export const downloadSinglePriceTemplate = async () => {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Single Price");
    worksheet.addRow(SINGLE_PRICE_TEMPLATE_HEADERS);
    worksheet.getColumn(1).numFmt = "@";
    addAlphabetOnlyValidation(worksheet, "B", "HQ line");
    worksheet.dataValidations.add("D2:D1048576", {
      type: "list",
      allowBlank: true,
      formulae: [`"${SINGLE_PRICE_TEMPLATE_OPTIONS.join(",")}"`],
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "single_price_template.xlsx";
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading single price template:", error);
    throw error;
  }
};

/**
 * Export all margin rule audit logs.
 * @returns {Promise<void>}
 */
export const exportMarginRuleLogs = async () => {
  try {
    const rows = await fetchAllMarginRuleLogs();
    const csvContent = [
      MARGIN_RULE_LOG_EXPORT_HEADERS.map(csvEscape).join(","),
      ...rows.map((row) =>
        MARGIN_RULE_LOG_EXPORT_COLUMNS.map((column) => csvEscape(column.value(row))).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "margin_rule_logs_export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Real API implementation (uncomment when backend is ready):
    // const response = await postAPI(
    //   `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.marginRuleExport}`,
    //   { responseType: "blob" }
    // );
    //
    // const blob = new Blob([response.data], {
    //   type: "text/csv;charset=utf-8;",
    // });
    // const link = document.createElement("a");
    // const url = URL.createObjectURL(blob);
    //
    // link.setAttribute("href", url);
    // link.setAttribute("download", "margin_rule_logs_export.csv");
    // link.style.visibility = "hidden";
    // document.body.appendChild(link);
    // link.click();
    // document.body.removeChild(link);
  } catch (error) {
    console.error("Error exporting margin rule logs:", error);
    throw error;
  }
};

/**
 * Export all single price audit logs.
 * @returns {Promise<void>}
 */
export const exportSinglePriceLogs = async () => {
  try {
    const rows = await fetchAllSinglePriceLogs();
    const csvContent = [
      SINGLE_PRICE_LOG_EXPORT_HEADERS.map(csvEscape).join(","),
      ...rows.map((row) =>
        SINGLE_PRICE_LOG_EXPORT_COLUMNS.map((column) => csvEscape(column.value(row))).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "single_price_logs_export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting single price logs:", error);
    throw error;
  }
};

/**
 * Get margin rule logs
 * Note: Currently uses the generic getRuleLogs from ruleLogsApi
 * @returns {Promise<Array>} Array of margin rule logs
 */
export const getMarginRuleLogs = async () => {
  return [];
};
